<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'WrapupTemplate',
  },
})

const { t } = useI18n()
const { showToast } = useToast()

// Interfaces
interface WrapupTemplate {
  id: number
  name: string
  description?: string
  template: any
  isActive: boolean
  isDefault: boolean
  version: string
  createdAt: string
  updatedAt: string
  templateWrapups?: TemplateWrapup[]
}

interface TemplateWrapup {
  id: number
  templateId: number
  wrapupId: number
  isActive: boolean
  sortOrder: number
  isRequired: boolean
  wrapup?: WrapupCategory
}

interface WrapupCategory {
  id: number
  name: string
  description?: string
  path: string
  level: number
  isActive: boolean
  sortOrder: number
  groupStatusId?: number
  parentId?: number
  children?: WrapupCategory[]
  groupStatus?: {
    id: number
    name: string
    color?: string
    icon?: string
  }
}

// State
const selectedTemplate = ref<WrapupTemplate | null>(null)
const availableCategories = ref<WrapupCategory[]>([])
const saving = ref(false)
const showDialog = ref(false)
const showMappingDialog = ref(false)
const showCategoriesDialog = ref(false)
const currentTemplate = ref<WrapupTemplate | null>(null)
const isConfirmDialogVisible = ref(false)
const templateWrapupToDelete = ref<TemplateWrapup | null>(null)

// Form data for template
const templateFormData = ref({
  name: '',
  description: '',
  template: {},
  isActive: true,
  isDefault: false,
  version: '1.0.0',
})

// Form data for mapping
const mappingFormData = ref({
  wrapupId: null as number | null,
  sortOrder: 0,
  isRequired: false,
})

// Search and filter
const searchQuery = ref('')
const selectedIsActive = ref()

const isActiveOptions = computed(() => [
  { title: t('common.active'), value: true },
  { title: t('common.inactive'), value: false },
])

// Table headers
const headers = computed(() => [
  {
    title: t('wrapup.templateName'),
    key: 'name',
    sortable: true,
  },
  {
    title: t('wrapup.version'),
    key: 'version',
    sortable: true,
  },
  {
    title: t('wrapup.categories'),
    key: 'categoriesCount',
    sortable: false,
  },
  {
    title: t('common.status'),
    key: 'isActive',
    sortable: true,
  },
  {
    title: t('wrapup.defaultTemplate'),
    key: 'isDefault',
    sortable: true,
  },
  {
    title: t('common.createdAt'),
    key: 'createdAt',
    sortable: true,
  },
  {
    title: t('common.actions'),
    key: 'actions',
    sortable: false,
  },
])

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Fetching wrapup group statuses
const { data: templatesData, execute: fetchTemplates, isFetching: loading } = useApi<any>(createUrl('/wrapup-template', {
  query: {
    itemsPerPage,
    page,
    q: searchQuery,
    isActive: selectedIsActive,
    sortBy,
    orderBy,
  },
}))

const templates = computed(() => templatesData.value?.data || [])
const totalItems = computed(() => templatesData.value?.total || 0)

// Computed properties

const availableCategoriesForMapping = computed(() => {
  if (!currentTemplate.value)
    return availableCategories.value

  // Get already mapped wrapup IDs
  const mappedIds = currentTemplate.value.templateWrapups?.map(tw => tw.wrapupId) || []

  // Filter out already mapped categories
  return availableCategories.value.filter(category => !mappedIds.includes(category.id))
})

const sortedTemplateMappings = computed(() => {
  if (!currentTemplate.value?.templateWrapups)
    return []

  const mappings = currentTemplate.value.templateWrapups

  // Build hierarchical structure
  const buildHierarchy = (mappings: any[]) => {
    const result: any[] = []

    // First, get all level 0 mappings (parents) sorted by sortOrder
    const parents = mappings
      .filter(m => m.wrapup?.level === 0)
      .sort((a, b) => a.sortOrder - b.sortOrder)

    // For each parent, add it and all its children
    for (const parent of parents) {
      result.push(parent)

      // Find all children of this parent recursively
      const addChildren = (parentId: number, level: number) => {
        const children = mappings
          .filter(m => {
            // Find children by checking parentId directly
            return m.wrapup?.parentId === parentId
                   && m.wrapup?.level === level + 1
          })
          .sort((a, b) => a.sortOrder - b.sortOrder)

        for (const child of children) {
          result.push(child)

          // Recursively add grandchildren
          addChildren(child.wrapup.id, child.wrapup.level)
        }
      }

      addChildren(parent.wrapup.id, 0)
    }

    return result
  }

  return buildHierarchy(mappings)
})

const fetchCategories = async () => {
  try {
    const { data } = await useApi('/wrapup/all-active')
    const allCategories = Array.isArray(data.value) ? data.value : []

    // Filter only level 0 categories (root categories)
    availableCategories.value = allCategories.filter(category => category.level === 0)
  }
  catch (error) {
    console.error('Failed to fetch categories:', error)
    showToast({
      type: 'error',
      message: t('wrapup.failedToLoadCategories'),
    })
  }
}

const openCategoriesDialog = (template: WrapupTemplate) => {
  currentTemplate.value = template
  showCategoriesDialog.value = true
}

const openCreateDialog = () => {
  selectedTemplate.value = null
  templateFormData.value = {
    name: '',
    description: '',
    template: {},
    isActive: true,
    isDefault: false,
    version: '1.0.0',
  }
  showDialog.value = true
}

const openEditDialog = (template: WrapupTemplate) => {
  selectedTemplate.value = template
  templateFormData.value = {
    name: template.name,
    description: template.description || '',
    template: template.template,
    isActive: template.isActive,
    isDefault: template.isDefault,
    version: template.version,
  }
  showDialog.value = true
}

const saveTemplate = async () => {
  if (!templateFormData.value.name.trim()) {
    showToast({
      type: 'error',
      message: t('wrapup.pleaseEnterTemplateName'),
    })

    return
  }

  saving.value = true
  try {
    if (selectedTemplate.value) {
      // Update existing template
      await $api(`/wrapup-template/${selectedTemplate.value.id}`, {
        method: 'PATCH',
        body: templateFormData.value,
      })
      showToast({
        type: 'success',
        message: t('wrapup.templateUpdatedSuccessfully'),
      })
    }
    else {
      // Create new template
      await $api('/wrapup-template', {
        method: 'POST',
        body: templateFormData.value,
      })
      showToast({
        type: 'success',
        message: t('wrapup.templateCreatedSuccessfully'),
      })
    }

    showDialog.value = false
    await fetchTemplates()
  }
  catch (error) {
    console.error('Failed to save template:', error)
    showToast({
      type: 'error',
      message: t('wrapup.failedToSaveTemplate'),
    })
  }
  finally {
    saving.value = false
  }
}

const toggleTemplateStatus = async (template: WrapupTemplate) => {
  try {
    await $api(`/wrapup-template/${template.id}/toggle-active`, {
      method: 'PATCH',
    })
    showToast({
      type: 'success',
      message: t('wrapup.templateStatusToggled'),
    })
    await fetchTemplates()

    // Refresh current template if it's the same one
    if (currentTemplate.value?.id === template.id) {
      const updatedTemplate = templates.value.find(t => t.id === template.id)
      if (updatedTemplate)
        currentTemplate.value = updatedTemplate
    }
  }
  catch (error) {
    console.error('Failed to toggle template status:', error)
    showToast({
      type: 'error',
      message: t('wrapup.failedToToggleTemplateStatus'),
    })
  }
}

const openMappingDialog = (template: WrapupTemplate) => {
  currentTemplate.value = template
  mappingFormData.value = {
    wrapupId: null,
    sortOrder: (template.templateWrapups?.length || 0) + 1,
    isRequired: false,
  }
  showMappingDialog.value = true
}

const addCategoryToTemplate = async () => {
  if (!currentTemplate.value || !mappingFormData.value.wrapupId) {
    showToast({
      type: 'error',
      message: t('wrapup.pleaseSelectCategory'),
    })

    return
  }

  saving.value = true
  try {
    const response = await $api('/template-wrapup', {
      method: 'POST',
      body: {
        templateId: currentTemplate.value.id,
        wrapupId: mappingFormData.value.wrapupId,
        sortOrder: mappingFormData.value.sortOrder,
        isRequired: mappingFormData.value.isRequired,
      },
    })

    const childrenCount = response?.childrenAdded || 0

    const message = childrenCount > 0
      ? t('wrapup.categoryAndChildrenAddedSuccessfully', { count: childrenCount })
      : t('wrapup.categoryAddedSuccessfully')

    showToast({
      type: 'success',
      message,
    })

    showMappingDialog.value = false
    await fetchTemplates()

    // Refresh current template
    if (currentTemplate.value) {
      const updatedTemplate = templates.value.find(t => t.id === currentTemplate.value!.id)
      if (updatedTemplate)
        currentTemplate.value = updatedTemplate
    }
  }
  catch (error) {
    console.error('Failed to add category to template:', error)
    showToast({
      type: 'error',
      message: t('wrapup.failedToAddCategoryToTemplate'),
    })
  }
  finally {
    saving.value = false
  }
}

const removeCategoryFromTemplate = (templateWrapup: TemplateWrapup) => {
  templateWrapupToDelete.value = templateWrapup
  isConfirmDialogVisible.value = true
}

const handleConfirmDelete = async () => {
  if (!templateWrapupToDelete.value)
    return

  try {
    await $api(`/template-wrapup/${templateWrapupToDelete.value.id}`, {
      method: 'DELETE',
    })

    showToast({
      type: 'success',
      message: t('wrapup.categoryRemovedFromTemplateSuccessfully'),
    })

    await fetchTemplates()

    // Refresh current template
    if (currentTemplate.value) {
      const updatedTemplate = templates.value.find(t => t.id === currentTemplate.value!.id)
      if (updatedTemplate)
        currentTemplate.value = updatedTemplate
    }
  }
  catch (error) {
    console.error('Failed to remove category from template:', error)
    showToast({
      type: 'error',
      message: t('wrapup.failedToRemoveCategoryFromTemplate'),
    })
  }
  finally {
    isConfirmDialogVisible.value = false
    templateWrapupToDelete.value = null
  }
}

const updateTemplateWrapup = async (templateWrapup: TemplateWrapup, field: string, value: any) => {
  try {
    await $api(`/template-wrapup/${templateWrapup.id}`, {
      method: 'PATCH',
      body: {
        [field]: value,
      },
    })

    showToast({
      type: 'success',
      message: t('common.updateSuccessfully'),
    })

    await fetchTemplates()

    // Refresh current template
    if (currentTemplate.value) {
      const updatedTemplate = templates.value.find(t => t.id === currentTemplate.value!.id)
      if (updatedTemplate)
        currentTemplate.value = updatedTemplate
    }
  }
  catch (error) {
    console.error('Failed to update template wrapup:', error)
    showToast({
      type: 'error',
      message: t('common.failedToUpdate'),
    })
  }
}

onMounted(async () => {
  await fetchTemplates()
  await fetchCategories()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('wrapup.wrapupTemplates') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('wrapup.manageWrapupTemplates') }}
      </p>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select IsActive -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedIsActive"
              :placeholder="t('common.selectStatus')"
              :items="isActiveOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem;">
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('wrapup.searchWrapupTemplates')"
            />
          </div>

          <!-- 👉 Add wrapup template button -->
          <VBtn
            color="primary"
            @click="openCreateDialog"
          >
            <VIcon
              start
              icon="tabler-plus"
            />
            {{ t('wrapup.createNewTemplate') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- 👉 Templates Table -->
      <VCardText>
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          v-model:sort-by="sortBy"
          :headers="headers"
          :items="templates"
          :items-length="totalItems"
          :loading="loading"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <!-- Name Column -->
          <template #item.name="{ item }">
            <div class="d-flex align-center">
              <VAvatar
                :color="item.isActive ? 'success' : 'error'"
                size="32"
                class="me-3"
              >
                <VIcon
                  :icon="item.isDefault ? 'tabler-star' : 'tabler-template'"
                  size="16"
                />
              </VAvatar>
              <div>
                <h6 class="text-base font-weight-medium mb-1">
                  {{ item.name }}
                  <VChip
                    v-if="item.isDefault"
                    size="x-small"
                    color="warning"
                    class="ms-2"
                  >
                    Default
                  </VChip>
                </h6>
                <span class="text-sm text-medium-emphasis">
                  {{ item.description || t('common.noDescription') }}
                </span>
              </div>
            </div>
          </template>

          <!-- Version Column -->
          <template #item.version="{ item }">
            <VChip
              size="small"
              color="info"
              variant="outlined"
            >
              {{ item.version }}
            </VChip>
          </template>

          <!-- Categories Count Column -->
          <template #item.categoriesCount="{ item }">
            <VChip
              size="small"
              color="primary"
              variant="tonal"
            >
              {{ item.templateWrapups?.length || 0 }} {{ t('wrapup.categories') }}
            </VChip>
          </template>

          <!-- Is Active Column -->
          <template #item.isActive="{ item }">
            <VChip
              :color="item.isActive ? 'success' : 'error'"
              size="small"
              variant="tonal"
            >
              {{ item.isActive ? t('common.active') : t('common.inactive') }}
            </VChip>
          </template>

          <!-- Is Default Column -->
          <template #item.isDefault="{ item }">
            <VChip
              v-if="item.isDefault"
              size="small"
              color="warning"
              variant="tonal"
            >
              {{ t('wrapup.defaultTemplate') }}
            </VChip>
            <span
              v-else
              class="text-medium-emphasis"
            >-</span>
          </template>

          <!-- Created At Column -->
          <template #item.createdAt="{ item }">
            <span class="text-sm">{{ formatDate24h(item.createdAt) }}</span>
          </template>

          <!-- Actions Column -->
          <template #item.actions="{ item }">
            <div class="d-flex gap-1">
              <VBtn
                icon="tabler-edit"
                size="small"
                variant="text"
                color="primary"
                @click="openEditDialog(item)"
              />
              <VBtn
                icon="tabler-link"
                size="small"
                variant="text"
                color="info"
                @click="openCategoriesDialog(item)"
              />
              <VBtn
                :icon="item.isActive ? 'tabler-eye-off' : 'tabler-eye'"
                size="small"
                variant="text"
                :color="item.isActive ? 'warning' : 'success'"
                @click="toggleTemplateStatus(item)"
              />
            </div>
          </template>

          <!-- pagination -->
          <template #bottom>
            <TablePagination
              v-model:page="page"
              :items-per-page="itemsPerPage"
              :total-items="totalItems"
            />
          </template>
        </VDataTableServer>
      </VCardText>
    </VCard>

    <!-- 👉 Create/Edit Template Dialog -->
    <VDialog
      v-model="showDialog"
      max-width="600px"
    >
      <VCard>
        <VCardTitle>
          {{ selectedTemplate ? t('wrapup.editTemplate') : t('wrapup.createNewTemplate') }}
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveTemplate">
            <VTextField
              v-model="templateFormData.name"
              :label="t('wrapup.templateName')"
              :placeholder="t('wrapup.enterTemplateName')"
              required
              :rules="[v => !!v || t('wrapup.pleaseEnterTemplateName')]"
              class="mb-4"
            />

            <VTextarea
              v-model="templateFormData.description"
              :label="t('wrapup.description')"
              :placeholder="t('wrapup.enterTemplateDescription')"
              rows="3"
              class="mb-4"
            />

            <VRow>
              <VCol cols="6">
                <VTextField
                  v-model="templateFormData.version"
                  :label="t('wrapup.version')"
                  placeholder="1.0.0"
                  class="mb-4"
                />
              </VCol>
              <VCol cols="6">
                <VSwitch
                  v-model="templateFormData.isDefault"
                  :label="t('wrapup.defaultTemplate')"
                  color="warning"
                  class="mb-4"
                />
              </VCol>
            </VRow>

            <VSwitch
              v-model="templateFormData.isActive"
              :label="t('common.status')"
              color="success"
            />
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            color="error"
            variant="outlined"
            @click="showDialog = false"
          >
            {{ t('common.cancel') }}
          </VBtn>

          <VBtn
            color="primary"
            :loading="saving"
            :disabled="saving"
            @click="saveTemplate"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Add Category to Template Dialog -->
    <VDialog
      v-model="showMappingDialog"
      max-width="500px"
    >
      <VCard>
        <VCardTitle>
          {{ t('wrapup.addCategoryToTemplate') }}
        </VCardTitle>

        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
          >
            <VIcon
              icon="tabler-info-circle"
              class="me-2"
            />
            {{ t('wrapup.selectOnlyMainCategories') }}
          </VAlert>
          <VForm @submit.prevent="addCategoryToTemplate">
            <VSelect
              v-model="mappingFormData.wrapupId"
              :label="t('wrapup.selectMainCategory')"
              :items="availableCategoriesForMapping"
              item-title="name"
              item-value="id"
              :placeholder="t('wrapup.selectMainCategoryToAdd')"
              required
              :rules="[v => !!v || t('wrapup.pleaseSelectCategory')]"
              class="mb-4"
            >
              <template #item="{ props, item }">
                <VListItem v-bind="props">
                  <div class="d-flex align-center">
                    <VIcon
                      :color="item.raw.isActive ? 'success' : 'error'"
                      :icon="item.raw.isActive ? 'tabler-folder' : 'tabler-folder-off'"
                      size="16"
                      class="me-3"
                    />
                    <div>
                      <span>{{ item.raw.name }}</span>
                      <div
                        v-if="item.raw.description"
                        class="text-caption text-medium-emphasis"
                      >
                        {{ item.raw.description }}
                      </div>
                    </div>
                  </div>
                </VListItem>
              </template>
            </VSelect>

            <VTextField
              v-model.number="mappingFormData.sortOrder"
              :label="t('wrapup.sortOrder')"
              type="number"
              placeholder="0"
              class="mb-4"
            />

            <VSwitch
              v-model="mappingFormData.isRequired"
              :label="t('wrapup.requiredCategory')"
              color="warning"
            />
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            color="error"
            variant="outlined"
            @click="showMappingDialog = false"
          >
            {{ t('common.cancel') }}
          </VBtn>

          <VBtn
            color="primary"
            :loading="saving"
            :disabled="saving"
            @click="addCategoryToTemplate"
          >
            {{ t('wrapup.addCategory') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Manage Categories Dialog -->
    <VDialog
      v-model="showCategoriesDialog"
      max-width="900px"
      fullscreen
    >
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <div>
            <VIcon
              icon="tabler-link"
              class="me-2"
            />
            {{ t('wrapup.manageCategories') }} - {{ currentTemplate?.name }}
          </div>
          <VBtn
            icon="tabler-x"
            variant="text"
            @click="showCategoriesDialog = false"
          />
        </VCardTitle>

        <VCardText>
          <div
            v-if="!currentTemplate"
            class="text-center py-8"
          >
            <VProgressCircular indeterminate />
            <p class="text-sm mt-2">
              {{ t('common.loading') }}
            </p>
          </div>

          <div v-else>
            <!-- Add Category Button -->
            <div class="d-flex justify-end mb-4">
              <VBtn
                color="primary"
                @click="openMappingDialog(currentTemplate)"
              >
                <VIcon
                  start
                  icon="tabler-plus"
                />
                {{ t('wrapup.addCategory') }}
              </VBtn>
            </div>

            <!-- Categories List -->
            <div
              v-if="!currentTemplate.templateWrapups?.length"
              class="text-center py-8"
            >
              <VAvatar
                color="grey-lighten-2"
                size="60"
                class="mb-3"
              >
                <VIcon>tabler-link-off</VIcon>
              </VAvatar>
              <h6 class="text-h6 mb-2">
                {{ t('wrapup.noLinkedCategories') }}
              </h6>
              <p class="text-sm text-medium-emphasis">
                {{ t('wrapup.clickAddCategoryToStart') }}
              </p>
            </div>

            <div v-else>
              <!-- Hierarchical Tree View -->
              <div class="template-mappings">
                <div
                  v-for="mapping in sortedTemplateMappings"
                  :key="mapping.id"
                  class="mapping-item"
                  :class="{
                    'mapping-parent': mapping.wrapup?.level === 0,
                    'mapping-child': mapping.wrapup?.level > 0,
                    [`mapping-level-${mapping.wrapup?.level}`]: true,
                  }"
                >
                  <div class="mapping-content">
                    <!-- Indentation for children -->
                    <div
                      v-if="mapping.wrapup?.level > 0"
                      class="mapping-indent"
                      :style="{ marginLeft: `${mapping.wrapup.level * 20}px` }"
                    >
                      <VIcon
                        icon="tabler-corner-down-right"
                        size="16"
                        class="me-1"
                      />
                    </div>

                    <!-- Category Info -->
                    <div class="mapping-info">
                      <div class="d-flex align-center">
                        <VIcon
                          :color="mapping.wrapup?.isActive ? 'success' : 'error'"
                          :icon="mapping.wrapup?.level === 0 ? 'tabler-folder' : 'tabler-folder-plus'"
                          size="16"
                          class="me-2"
                        />
                        <div>
                          <span
                            class="mapping-name"
                            :class="{ 'parent-name': mapping.wrapup?.level === 0 }"
                          >
                            {{ mapping.wrapup?.name }}
                          </span>
                          <VChip
                            v-if="mapping.wrapup?.level === 0"
                            size="x-small"
                            color="primary"
                            class="ms-2"
                          >
                            {{ t('wrapup.main') }}
                          </VChip>
                          <VChip
                            v-else
                            size="x-small"
                            color="info"
                            variant="outlined"
                            class="ms-2"
                          >
                            {{ t('wrapup.child') }}
                          </VChip>
                        </div>
                      </div>
                      <p
                        v-if="mapping.wrapup?.description"
                        class="mapping-description"
                      >
                        {{ mapping.wrapup.description }}
                      </p>
                    </div>

                    <!-- Controls -->
                    <div class="mapping-controls">
                      <VTextField
                        :model-value="mapping.sortOrder"
                        type="number"
                        density="compact"
                        variant="outlined"
                        style="width: 80px"
                        @blur="updateTemplateWrapup(mapping, 'sortOrder', parseInt($event.target.value))"
                      />

                      <VSwitch
                        :model-value="mapping.isRequired"
                        color="warning"
                        @update:model-value="updateTemplateWrapup(mapping, 'isRequired', $event)"
                      />

                      <VSwitch
                        :model-value="mapping.isActive"
                        color="success"
                        @update:model-value="updateTemplateWrapup(mapping, 'isActive', $event)"
                      />

                      <VBtn
                        icon="tabler-trash"
                        size="small"
                        variant="text"
                        color="error"
                        @click="removeCategoryFromTemplate(mapping)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            color="error"
            variant="outlined"
            @click="showCategoriesDialog = false"
          >
            {{ t('common.close') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Confirm Delete Dialog -->
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogVisible"
      :confirmation-question="templateWrapupToDelete ? t('wrapup.confirmRemoveCategoryFromTemplate', { name: templateWrapupToDelete.wrapup?.name }) : ''"
      :confirm-title="t('common.delete')"
      :confirm-msg="t('wrapup.categoryRemovedFromTemplateSuccessfully')"
      :cancel-title="t('common.cancel')"
      :cancel-msg="t('common.cancel')"
      @confirm="handleConfirmDelete"
    />
  </section>
</template>

<style scoped>
/* Template Mappings Styles */
.template-mappings {
  .mapping-item {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 8px;
    margin-bottom: 8px;
    transition: all 0.2s ease-in-out;

    &:hover {
      box-shadow: 0 2px 8px rgba(var(--v-shadow-key-umbra-opacity), 0.1);
    }

    &.mapping-parent {
      background-color: rgba(var(--v-theme-primary), 0.04);
      border-color: rgba(var(--v-theme-primary), 0.3);

      .parent-name {
        font-weight: 600;
        color: rgb(var(--v-theme-primary));
      }
    }

    &.mapping-child {
      background-color: rgba(var(--v-theme-surface-variant), 0.3);
      border-color: rgba(var(--v-theme-outline), 0.2);
    }

    .mapping-content {
      display: flex;
      align-items: center;
      padding: 12px;
      gap: 12px;

      .mapping-indent {
        display: flex;
        align-items: center;
        color: rgba(var(--v-theme-outline), 0.6);
        flex-shrink: 0;
      }

      .mapping-info {
        flex: 1;
        min-width: 0;

        .mapping-name {
          font-size: 14px;
          line-height: 1.4;
        }

        .mapping-description {
          font-size: 12px;
          color: rgba(var(--v-theme-on-surface), 0.6);
          margin: 4px 0 0 0;
          line-height: 1.3;
        }
      }

      .mapping-controls {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;

        .v-text-field {
          .v-field {
            min-height: 32px;
          }
        }

        .v-switch {
          .v-switch__track {
            height: 20px;
            width: 36px;
          }

          .v-switch__thumb {
            height: 16px;
            width: 16px;
          }
        }
      }
    }
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .template-mappings {
    .mapping-item {
      .mapping-content {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;

        .mapping-controls {
          justify-content: space-between;
          flex-wrap: wrap;
        }
      }
    }
  }
}
</style>
